/** @jsxImportSource theme-ui @jsxRuntime classic */
import {
  Box,
  Button,
  CarouselV2,
  CarouselV2ContextProvider,
  Flex,
  getThemeByName,
  Modal,
  SanityRichText,
  ThemeProvider,
  useResponsiveValue,
} from '@tishman/components';
import React, { useState, useEffect } from 'react';

import { TicketComparisonModal } from '../../TicketComparisonModal';

import { SidebarMenu } from './SidebarMenu';
import { CategoryHeader } from './CategoryHeader';
import { TicketCard } from './TicketCard';

import type { ReactNode } from 'react';

export interface BuyTicketsCarouselProps {
  categories: Queries.BuyTicketsQuery['allSanityTicketCategory']['nodes'];
  category: Queries.SanityTicketCategory;
}

const SpaceBox = ({ children }: { children?: ReactNode }) => (
  <Box sx={{ flex: ['0 0 20%', null, '0 0 15%'] }}>{children}</Box>
);

export function BuyTicketsCarousel({
  categories,
  category,
}: BuyTicketsCarouselProps): React.JSX.Element | null {
  const [hasMounted, setHasMounted] = useState(false);
  const [isComparisonModalOpen, setIsComparisonModalOpen] = useState(false);
  const categoryTicketsLength = category.tickets?.length ?? 0;
  const defaultPageSize = useResponsiveValue([
    1,
    1,
    2,
    3,
    3,
    categoryTicketsLength > 4 ? 4.2 : 4,
  ]);

  const minDesktopSize = categoryTicketsLength == 1 ? 2 : categoryTicketsLength;

  const pageSize =
    categoryTicketsLength < defaultPageSize ? minDesktopSize : defaultPageSize;

  useEffect(() => {
    setHasMounted(true);
  }, []);

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        width: '100%',
      }}
    >
      <Flex>
        <SpaceBox />
        <CategoryHeader
          carouselTitle={category.title || ''}
          description={
            <SanityRichText
              blocks={
                category.description && Array.isArray(category.description)
                  ? category.description
                  : []
              }
            />
          }
        />
      </Flex>
      <Flex>
        <SpaceBox>
          <SidebarMenu
            categories={categories}
            category={
              category as Queries.BuyTicketsQuery['allSanityTicketCategory']['nodes'][0]
            }
          />
        </SpaceBox>
        <Flex
          sx={{
            flexDirection: 'column',
            position: 'relative',
            pb: '0px',
            flex: ['0 0 80%', null, '0 0 85%'],
            opacity: !hasMounted ? 0 : 1,
          }}
        >
          <CarouselV2ContextProvider>
            <CarouselV2
              containerStyle={{
                height: '100%',
                paddingLeft: '32px',
                paddingRight: '32px',
              }}
              slideStyle={{ height: 'auto' }}
              swiperOptions={{
                slidesPerView: pageSize,
                spaceBetween: 32,
              }}
            >
              {category.tickets?.map((ticket) => (
                <TicketCard
                  category={category}
                  key={ticket?.id}
                  ticket={ticket}
                />
              ))}
            </CarouselV2>
          </CarouselV2ContextProvider>

          {!!category.availableFeatures?.length && (
            <Box
              sx={{
                alignSelf: 'center',
                display: 'flex',
                justifyContent: 'flex-end',
                mt: [4, 0],
                mb: ['0px'],
                position: 'static',
                width: '100%',
                px: '32px',
                pb: '0px',
              }}
            >
              <Button
                onClick={() => {
                  setIsComparisonModalOpen(
                    (isComparisonModalOpen) => !isComparisonModalOpen,
                  );
                }}
                sx={{
                  width: '100%',
                  height: '52px',
                  mt: 4,
                  py: '0px',
                  px: '0px',
                }}
              >
                Compare Tickets
              </Button>
              <ThemeProvider theme={getThemeByName('Rock Center')}>
                <Modal
                  id={`buy-tickets-desktop-ticket-comparison-modal-${category.id}`}
                  isOpen={isComparisonModalOpen}
                  onClose={() => setIsComparisonModalOpen(false)}
                >
                  <TicketComparisonModal
                    category={
                      category as unknown as
                        | Queries.BuyTicketsQuery['allSanityTicketCategory']['nodes'][0]
                        | Queries.AttractionTorQuery['allSanityTicketCategory']['nodes'][0]
                    }
                    onClose={() => setIsComparisonModalOpen(false)}
                  />
                </Modal>
              </ThemeProvider>
            </Box>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
